import { useCallback, useEffect, useRef, useState } from 'react';
import { getItem, removeItem, setItem } from '@/utils/storage';

type InitialValue<T> = T | (() => T);

/** Same-tab subscribers, keyed by storage key (the `storage` event is cross-tab only). */
const listeners = new Map<string, Set<(value: unknown) => void>>();

function subscribe(
	key: string,
	listener: (value: unknown) => void,
): () => void {
	let set = listeners.get(key);
	if (!set) {
		set = new Set();
		listeners.set(key, set);
	}
	set.add(listener);

	return () => {
		set?.delete(listener);
		if (set && set.size === 0) listeners.delete(key);
	};
}

function emit(key: string, value: unknown): void {
	listeners.get(key)?.forEach((listener) => listener(value));
}

function resolveInitial<T>(initialValue: InitialValue<T>): T {
	return typeof initialValue === 'function'
		? (initialValue as () => T)()
		: initialValue;
}

export interface UseLocalStorageResult<T> {
	value: T;
	/** Set a value or use an updater; persists immediately. */
	setValue: (value: T | ((previous: T) => T)) => void;
	/** Remove the key and reset to the initial value. */
	removeValue: () => void;
}

/**
 * Persist a piece of state in `localStorage`.
 *
 * - Fail-safe: environments without storage (privacy mode/SSR) fall back to
 *   in-memory state via the shared `utils/storage` wrappers.
 * - Content is JSON-encoded; invalid stored JSON falls back to `initialValue`.
 * - Syncs across tabs (`storage` event) and between components in the same tab.
 *
 * Use it for small, non-sensitive client preferences/drafts. Never store
 * secrets, tokens or server data that belongs in the query cache.
 *
 * @param key storage key
 * @param initialValue value used when nothing valid is stored
 */
export function useLocalStorage<T>(
	key: string,
	initialValue: InitialValue<T>,
): UseLocalStorageResult<T> {
	const initialRef = useRef(initialValue);

	const [value, setValueState] = useState<T>(() =>
		getItem(key, resolveInitial(initialValue)),
	);
	const valueRef = useRef(value);
	valueRef.current = value;

	// Re-read when the key changes.
	useEffect(() => {
		const next = getItem(key, resolveInitial(initialRef.current));
		valueRef.current = next;
		setValueState(next);
	}, [key]);

	const setValue = useCallback(
		(next: T | ((previous: T) => T)) => {
			const resolved =
				typeof next === 'function'
					? (next as (previous: T) => T)(valueRef.current)
					: next;

			valueRef.current = resolved;
			setValueState(resolved);
			setItem(key, resolved);
			emit(key, resolved);
		},
		[key],
	);

	const removeValue = useCallback(() => {
		const next = resolveInitial(initialRef.current);
		valueRef.current = next;
		setValueState(next);
		removeItem(key);
		emit(key, next);
	}, [key]);

	// Same-tab subscribers.
	useEffect(
		() =>
			subscribe(key, (next) => {
				valueRef.current = next as T;
				setValueState(next as T);
			}),
		[key],
	);

	// Cross-tab updates.
	useEffect(() => {
		if (typeof window === 'undefined') return;

		const onStorage = (event: StorageEvent) => {
			if (event.key !== key) return;
			const next = getItem(key, resolveInitial(initialRef.current));
			valueRef.current = next;
			setValueState(next);
		};

		window.addEventListener('storage', onStorage);
		return () => window.removeEventListener('storage', onStorage);
	}, [key]);

	return { value, setValue, removeValue };
}
